import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { DocumentArrowUpIcon, TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import DashboardHeader from "../../../../components/dashboard/dashboardHeader";
import { ResourceLoading } from "../../../../components/utils/ResourceLoading";
import { StatusUpdateSection } from "../../../../components/dashboard/statusUpdates/StatusUpdateSection";
import {
  deleteJobApplication,
  fetchJobApplication,
  updateJobApplication,
} from "../../../../api/jobApplications";
import { uploadModuleDocuments } from "../../../../api/moduleDocumentUpload";
import { RequirementChecksEditor } from "../../../../components/dashboard/hr/RequirementChecklist";
import { requirementChecksFromSource } from "../../../../utils/jobOpeningRequirements";
import {
  CANDIDATE_STATUSES,
  DetailRow,
  SectionCard,
  STATUS_STYLES,
} from "./shared";

export const JobOpeningCandidateShow = () => {
  const { t } = useTranslation();
  const { id, candidateId } = useParams();
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState(null);
  const [docs, setDocs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [requirementChecks, setRequirementChecks] = useState([]);
  const [savingChecks, setSavingChecks] = useState(false);
  const openingPath = `/dashboard/hr/job-openings/${id}`;

  const loadCandidate = useCallback(async () => {
    const response = await fetchJobApplication(candidateId);
    if (!response?.id) {
      toast.error(t("hr.candidates.load_failed"));
      return null;
    }
    if (String(response.job_opening_id) !== String(id)) {
      toast.error(t("hr.candidates.not_found"));
      return null;
    }
    setCandidate(response);
    setDocs(response.documents || []);
    setRequirementChecks(
      requirementChecksFromSource(response.job_opening, response.requirement_checks)
    );
    return response;
  }, [candidateId, id, t]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      const loaded = await loadCandidate();
      setIsLoading(false);
      if (!loaded) navigate(openingPath, { replace: true });
    })();
  }, [loadCandidate, navigate, openingPath]);

  const handleUpdateStatus = async (status) => {
    if (!candidate?.id || status === candidate.status) return;
    const response = await updateJobApplication({ id: candidate.id, status });
    if (response?.id) {
      setCandidate(response);
      setDocs(response.documents || docs);
      toast.success(t("hr.candidates.updated"));
    } else {
      toast.error(t("hr.candidates.update_failed"));
    }
  };

  const handleDelete = async () => {
    if (!candidate?.id) return;
    if (!window.confirm(t("hr.candidates.delete_confirm"))) return;
    const response = await deleteJobApplication(candidate.id);
    if (response?.success) {
      toast.success(t("hr.candidates.deleted"));
      navigate(openingPath);
    } else {
      toast.error(t("hr.candidates.delete_failed"));
    }
  };

  const handleSaveRequirements = async () => {
    if (!candidate?.id) return;
    const payload = requirementChecks
      .map((item) => ({
        id: item.id,
        text: String(item.text || "").trim(),
        checked: !!item.checked,
      }))
      .filter((item) => item.text);
    setSavingChecks(true);
    try {
      const response = await updateJobApplication({
        id: candidate.id,
        requirement_checks: payload,
      });
      if (response?.id) {
        setCandidate(response);
        setRequirementChecks(
          requirementChecksFromSource(response.job_opening, response.requirement_checks)
        );
        toast.success(t("hr.candidates.updated"));
      } else {
        toast.error(t("hr.candidates.update_failed"));
      }
    } finally {
      setSavingChecks(false);
    }
  };

  const handleUploadDocs = async (files) => {
    if (!candidate?.id || !files?.length) return;
    try {
      setUploading(true);
      await uploadModuleDocuments("job_application", candidate.id, [...files]);
      toast.success(t("hr.candidates.docs_uploaded"));
      await loadCandidate();
    } catch (e) {
      toast.error(t("hr.candidates.docs_upload_failed"));
    } finally {
      setUploading(false);
    }
  };

  if (isLoading || !candidate) {
    return (
      <div className="py-20">
        <ResourceLoading />
      </div>
    );
  }

  const openingTitle =
    candidate.job_opening?.title || t("hr.job_openings.title");

  return (
    <DashboardHeader
      variant="v2"
      title={candidate.full_name}
      subtitle={openingTitle}
      backUrl={openingPath}
      meta={[
        {
          label: t("hr.candidates.fields.status"),
          value:
            candidate.status_label ||
            t(`hr.candidates.statuses.${candidate.status}`),
          hidden: false,
        },
      ]}
      actions={[
        {
          label: t("hr.common.delete"),
          icon: TrashIcon,
          action: handleDelete,
          hidden: false,
          menu: true,
        },
      ]}
    >
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
          <div className="space-y-5">
            <SectionCard title={t("hr.candidates.sections.details")}>
              <dl>
                <DetailRow
                  label={t("hr.candidates.fields.email")}
                  value={candidate.email}
                />
                <DetailRow
                  label={t("hr.candidates.fields.phone")}
                  value={candidate.phone}
                />
                <DetailRow
                  label={t("hr.candidates.fields.city")}
                  value={candidate.city}
                />
                <DetailRow
                  label={t("hr.candidates.fields.linkedin")}
                  value={candidate.linkedin_url}
                />
                <DetailRow
                  label={t("hr.candidates.fields.source")}
                  value={
                    candidate.source_label ||
                    (candidate.source
                      ? t(`hr.candidates.sources.${candidate.source}`, {
                          defaultValue: candidate.source,
                        })
                      : null)
                  }
                />
                <DetailRow
                  label={t("hr.candidates.fields.applied_at")}
                  value={
                    candidate.applied_at
                      ? new Date(candidate.applied_at).toLocaleString("nb-NO")
                      : null
                  }
                />
              </dl>
            </SectionCard>

            <SectionCard
              title={t("hr.candidates.fields.requirements")}
              action={
                candidate.source === "website" ? null : (
                  <button
                    type="button"
                    disabled={savingChecks}
                    onClick={handleSaveRequirements}
                    className="rounded-lg bg-[#1463C7] px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-60"
                  >
                    {t("hr.common.save")}
                  </button>
                )
              }
            >
              <RequirementChecksEditor
                hint={
                  candidate.source === "website"
                    ? t("hr.candidates.fields.requirements_readonly_hint")
                    : t("hr.candidates.fields.requirements_toggle_hint")
                }
                items={requirementChecks}
                onChange={
                  candidate.source === "website" ? undefined : setRequirementChecks
                }
                emptyLabel={t("hr.common.empty")}
              />
            </SectionCard>

            {candidate.cover_letter ? (
              <SectionCard title={t("hr.candidates.fields.cover_letter")}>
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {candidate.cover_letter}
                </p>
              </SectionCard>
            ) : null}

            <SectionCard
              title={t("hr.candidates.documents")}
              action={
                <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                  <DocumentArrowUpIcon className="h-4 w-4" />
                  {uploading
                    ? t("hr.common.uploading")
                    : t("hr.candidates.upload")}
                  <input
                    type="file"
                    className="hidden"
                    multiple
                    accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                    disabled={uploading}
                    onChange={(e) => handleUploadDocs(e.target.files)}
                  />
                </label>
              }
            >
              {docs.length === 0 ? (
                <p className="text-sm text-slate-500">
                  {t("hr.candidates.no_docs")}
                </p>
              ) : (
                <ul className="space-y-2">
                  {docs.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    >
                      <span className="font-medium text-slate-800">
                        {doc.title || doc.document_file_name || `#${doc.id}`}
                      </span>
                      {doc.file && (
                        <a
                          href={doc.file}
                          target="_blank"
                          rel="noreferrer"
                          className="font-semibold text-[#1463C7]"
                        >
                          {t("hr.common.open")}
                        </a>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </SectionCard>

            {candidate.notes ? (
              <SectionCard title={t("hr.candidates.fields.legacy_note")}>
                <p className="whitespace-pre-wrap text-sm leading-6 text-slate-700">
                  {candidate.notes}
                </p>
              </SectionCard>
            ) : null}

            <section className="rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-[0_1px_2px_rgba(0,0,0,.04)]">
              <p className="mb-3 text-sm text-slate-500">
                {t("hr.candidates.sections.history_hint")}
              </p>
              <StatusUpdateSection
                subjectType="job_application"
                subjectId={candidate.id}
                title={t("hr.candidates.sections.history")}
              />
            </section>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-24 lg:self-start">
            <SectionCard title={t("hr.candidates.fields.status")}>
              <select
                className={`w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm font-semibold ${
                  STATUS_STYLES[candidate.status] || STATUS_STYLES.new
                }`}
                value={candidate.status}
                onChange={(e) => handleUpdateStatus(e.target.value)}
              >
                {CANDIDATE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {t(`hr.candidates.statuses.${status}`)}
                  </option>
                ))}
              </select>
            </SectionCard>
          </aside>
        </div>
      </div>
    </DashboardHeader>
  );
};
